import React, { useState, useEffect, useRef } from "react";
import { useTemplateStore } from "@/zustand/templateStore";
import { useProductData } from "@/zustand/productStore";
import { useCartData } from "@/zustand/cartStore.js";
import { StripeApplePayProvider } from "@/utils/applePay/applePayProvider";
import { CheckoutcomApplePayProvider } from "@/utils/applePay/checkoutcomApplePayProvider";

const TIMEOUT_MS = 30000;

const ApplePayButton = ({ height, onApplePaySubmit, requirePolicy }) => {
  const [isAvailable, setIsAvailable] = useState(false);
  const [paymentProvider, setPaymentProvider] = useState(null);

  const checkoutProviderRef = useRef(null);
  const stripeProviderRef = useRef(null);
  const onApplePaySubmitRef = useRef(onApplePaySubmit);
  onApplePaySubmitRef.current = onApplePaySubmit;
  const requirePolicyRef = useRef(requirePolicy);
  requirePolicyRef.current = requirePolicy;

  const { paymentsConfig, country_code, currency_code } = useProductData();
  const { selectedProduct, cartTotal } = useCartData();
  const {
    stripeApplePayAllowed,
    stripePublisherKey,
    dynamicDescriptor,
    applePayConfigured,
    appleMerchantId,
    checkoutcomApplePayConfigured,
    checkoutcomApplePayDisplayName,
  } = paymentsConfig || {};

  const setIsApplePayAvailable = useTemplateStore((s) => s.setIsApplePayAvailable);
  const handlePaymentMethodChange = useTemplateStore((s) => s.handlePaymentMethodChange);
  const setLoadingState = useTemplateStore((s) => s.setLoadingState);

  // Set provider based on configuration priority
  useEffect(() => {
    if (stripeApplePayAllowed) {
      setPaymentProvider("stripe");
    } else if (applePayConfigured) {
      setPaymentProvider("adyen");
    } else if (checkoutcomApplePayConfigured) {
      setPaymentProvider("checkoutcom");
    }
  }, [stripeApplePayAllowed, applePayConfigured, checkoutcomApplePayConfigured]);

  // Initialize Checkout.com Apple Pay (native ApplePaySession)
  useEffect(() => {
    if (paymentProvider !== "checkoutcom") return;

    const { paymentsConfig: config, currency_code: currency, country_code: country } = useProductData.getState();
    const provider = new CheckoutcomApplePayProvider();

    const init = async () => {
      try {
        provider.onAuthorized(async (event) => {
          useTemplateStore.getState().handlePaymentMethodChange("applepay");
          useTemplateStore.getState().setLoadingState(true);

          try {
            const token = event.payment?.token || {};
            const contact = event.payment?.shippingContact || event.payment?.billingContact || {};

            const applePayData = {
              apple_pay_token: JSON.stringify(token),
              applePayProvider: "checkoutcom-flow",
              customer: {
                first_name: contact.givenName || "",
                last_name: contact.familyName || "",
                email: contact.emailAddress || "",
                phone: contact.phoneNumber || "",
                address: contact.addressLines?.[0] || "",
                address2: contact.addressLines?.[1] || "",
                city: contact.locality || "",
                state: contact.administrativeArea || "",
                zip: contact.postalCode || "",
                country: contact.countryCode || "",
              },
            };

            if (onApplePaySubmitRef.current) {
              await onApplePaySubmitRef.current(applePayData);
            }
          } catch (e) {
            console.error("Checkout.com Apple Pay submit error:", e);
            throw e; // re-throw so session.completePayment gets STATUS_FAILURE
          } finally {
            useTemplateStore.getState().setLoadingState(false);
          }
        });

        provider.onError((error) => {
          if (error?.type === "cancel") return;
          console.error("Checkout.com Apple Pay error:", error);
          useTemplateStore.getState().setLoadingState(false);
        });

        // Fetch public key from config endpoint
        const configResp = await fetch("/wp-json/checkout/v1/order/checkoutcom/config");
        const configData = await configResp.json();

        await provider.initialize({
          amount: "0",
          currency: currency || "USD",
          countryCode: country || "US",
          displayName: config?.checkoutcomApplePayDisplayName || "Online Store",
          publicKey: configData.publicKey,
        });

        if (provider.isAvailable()) {
          checkoutProviderRef.current = provider;
          setIsAvailable(true);
          useTemplateStore.getState().setIsApplePayAvailable(true);
        }
      } catch (error) {
        console.error("Checkout.com Apple Pay init failed:", error);
      }
    };

    init();

    return () => {
      if (checkoutProviderRef.current) {
        checkoutProviderRef.current.unmount();
        checkoutProviderRef.current = null;
      }
    };
  }, [paymentProvider]);

  // Stripe provider
  useEffect(() => {
    if (paymentProvider !== "stripe") {
      return;
    }

    let isMounted = true;

    const initializeProvider = async () => {
      try {
        const applePayProvider = new StripeApplePayProvider(stripePublisherKey);
        await applePayProvider.initialize(selectedProduct, country_code, currency_code);
        const canMake = await applePayProvider.canMakePayment();

        if (!isMounted) return;

        if (canMake && stripeApplePayAllowed) {
          setIsApplePayAvailable(true);
          setIsAvailable(true);
          stripeProviderRef.current = applePayProvider;

          applePayProvider.onToken(async (event) => {
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), TIMEOUT_MS);

            try {
              const payerName = event.payerName || "";
              const nameParts = payerName.split(" ");
              const firstName = nameParts[0] || "";
              const lastName = nameParts.slice(1).join(" ") || nameParts[0] || "";

              const shippingAddress = event.shippingAddress;
              if (!shippingAddress) {
                return;
              }

              const updatedData = {
                paymentMethod: "applepay",
                applePayProvider: "stripe",
                apple_pay_token: JSON.stringify(event.token),
                customer: {
                  first_name: firstName,
                  last_name: lastName,
                  email: event.payerEmail || "",
                  phone: event.payerPhone || "",
                  country: shippingAddress.country || "US",
                  address:
                    `${shippingAddress.addressLine?.[0] || ""}${shippingAddress.addressLine?.[1] ? " " + shippingAddress.addressLine?.[1] : ""}`.trim(),
                  address2: "",
                  city: shippingAddress.city || "",
                  state: shippingAddress.region || shippingAddress.city || "",
                  postalCode: shippingAddress.postalCode || "",
                },
              };

              await onApplePaySubmitRef.current(updatedData, { signal: controller.signal });
            } catch (error) {
              console.error("Payment processing error:", error);
            } finally {
              clearTimeout(timeout);
            }
          });
        } else {
          stripeProviderRef.current = null;
        }
      } catch (error) {
        console.error("Error initializing Apple Pay provider:", error);
      }
    };

    initializeProvider();

    return () => {
      isMounted = false;
      stripeProviderRef.current = null;
    };
  }, [
    selectedProduct,
    country_code,
    currency_code,
    stripeApplePayAllowed,
    stripePublisherKey,
    paymentProvider,
    setIsApplePayAvailable,
  ]);

  // Adyen provider
  useEffect(() => {
    if (paymentProvider !== "adyen") {
      return;
    }

    let cancelled = false;

    const checkAvailability = async () => {
      try {
        if (!applePayConfigured || !window.ApplePaySession || !window.ApplePaySession.canMakePayments()) {
          setIsAvailable(false);
          return;
        }

        const hasActiveCard = await ApplePaySession.canMakePaymentsWithActiveCard(appleMerchantId || "");

        if (cancelled) return;

        if (!hasActiveCard) {
          setIsAvailable(false);
          return;
        }

        // Validate domain is whitelisted with Adyen before rendering
        const validateResp = await fetch("/wp-json/checkout/v1/order/applepay/validate", { method: "POST" });

        if (cancelled) return;

        if (!validateResp.ok) {
          setIsAvailable(false);
          return;
        }

        setIsAvailable(true);
        setIsApplePayAvailable(true);
      } catch (err) {
        console.error("Apple Pay availability check failed:", err);
        if (!cancelled) setIsAvailable(false);
      }
    };

    checkAvailability();

    return () => {
      cancelled = true;
    };
  }, [paymentProvider, applePayConfigured, setIsApplePayAvailable, appleMerchantId]);

  // Checkout.com: native Apple Pay button
  if (paymentProvider === "checkoutcom") {
    if (!isAvailable) return null;

    const handleCheckoutcomApplePayClick = async () => {
      handlePaymentMethodChange("applepay");

      if (typeof requirePolicy === "function") {
        const ok = await requirePolicy();
        if (!ok) return;
      }

      const provider = checkoutProviderRef.current;
      if (!provider) return;

      const { cartTotal: total } = useCartData.getState();
      const { currency_code: currency, country_code: country, paymentsConfig: config } = useProductData.getState();

      provider.config = {
        amount: total || "0",
        currency: currency || "USD",
        countryCode: country || "US",
        displayName: config?.checkoutcomApplePayDisplayName || "Online Store",
      };

      await provider.show();
    };

    return (
      <div className="applepay-container">
        <button
          type="button"
          onClick={handleCheckoutcomApplePayClick}
          style={{
            appearance: "-apple-pay-button",
            WebkitAppearance: "-apple-pay-button",
            applePayButtonStyle: "black",
            width: "100%",
            height: height || 48,
            borderRadius: 6,
            border: "none",
            cursor: "pointer",
          }}
        />
      </div>
    );
  }

  // UI gatekeeper for other providers
  if (
    (paymentProvider === "adyen" && !isAvailable) ||
    (paymentProvider === "stripe" && !isAvailable) ||
    paymentProvider === null
  ) {
    return null;
  }

  const handleStripeApplePaySubmit = async () => {
    try {
      await stripeProviderRef.current.show();
    } catch (error) {
      console.error("Error showing Apple Pay:", error);
    }
  };

  const handleAdyenApplePaySubmit = async () => {
    const { currency_code: currencyCode, country_code: countryCode } = useProductData.getState();
    const { cartTotal } = useCartData.getState();

    if (!cartTotal || parseFloat(cartTotal) <= 0) return;

    const paymentRequest = {
      currencyCode: currencyCode || "USD",
      countryCode: countryCode || "US",
      total: {
        label: dynamicDescriptor,
        amount: cartTotal || "0.00",
      },
      supportedNetworks: ["visa", "masterCard", "amex", "discover", "JCB"],
      merchantCapabilities: ["supports3DS"],
      requiredShippingContactFields: ["email", "phone", "postalAddress", "name"],
    };

    const session = new ApplePaySession(3, paymentRequest);

    session.onvalidatemerchant = async (event) => {
      const r = await fetch("/wp-json/checkout/v1/order/applepay/validate", { method: "POST" });
      const merchantSession = await r.json();
      session.completeMerchantValidation(merchantSession);
    };

    session.oncancel = () => {};

    session.onpaymentauthorized = async (event) => {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), TIMEOUT_MS);

      try {
        const applePayData = {
          apple_pay_token: JSON.stringify(event.payment.token),
          applePayProvider: "adyen",
          customer: {
            first_name: event.payment.shippingContact?.givenName || event.payment.billingContact?.givenName || "",
            last_name: event.payment.shippingContact?.familyName || event.payment.billingContact?.familyName || "",
            phone: event.payment.shippingContact?.phoneNumber || event.payment.billingContact?.phoneNumber || "",
            email: event.payment.shippingContact?.emailAddress || "",
            address: event.payment.shippingContact?.addressLines?.[0] || "",
            address2: event.payment.shippingContact?.addressLines?.[1] || "",
            city: event.payment.shippingContact?.locality || "",
            state: event.payment.shippingContact?.administrativeArea || "",
            zip: event.payment.shippingContact?.postalCode || "",
            country: event.payment.shippingContact?.countryCode || "",
          },
        };

        if (onApplePaySubmitRef.current) {
          await onApplePaySubmitRef.current(applePayData, { signal: controller.signal });
          session.completePayment(ApplePaySession.STATUS_SUCCESS);
        } else {
          session.completePayment(ApplePaySession.STATUS_FAILURE);
        }
      } catch (e) {
        console.error("Payment authorization error:", e);
        session.completePayment(ApplePaySession.STATUS_FAILURE);
      } finally {
        clearTimeout(timeout);
      }
    };

    session.begin();
  };

  const handleClick = async () => {
    handlePaymentMethodChange("applepay");

    if (typeof requirePolicy === "function") {
      const ok = await requirePolicy();
      if (!ok) return;
    }

    if (paymentProvider === "adyen") {
      await handleAdyenApplePaySubmit();
    } else if (paymentProvider === "stripe") {
      await handleStripeApplePaySubmit();
    }
  };

  return (
    <div className="applepay-container">
      <button
        type="button"
        onClick={handleClick}
        style={{
          appearance: "-apple-pay-button",
          WebkitAppearance: "-apple-pay-button",
          applePayButtonStyle: "black",
          width: "100%",
          height: height || 56,
          borderRadius: 6,
          border: "none",
          cursor: "pointer",
        }}
      />
    </div>
  );
};

export default ApplePayButton;
